import { FunctionalComponent } from 'preact';
import {
  EffectCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'preact/hooks';
import {
  useSelector,
  useCheckoutContext,
} from '@primer-io/shared-library/contexts';
import { Scene, SceneId } from '../enums/Checkout';
import { SceneStage } from '../store/BaseStore';
import { ModuleKeyDescriptor } from './ModuleFederation/types';
import { noop } from './noop';

export const usePrevious = (value) => {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const useIsFirstRender = () => {
  const ref = useRef(true);
  useLayoutEffect(() => {
    ref.current = false;
  });
  return ref.current;
};

export const useFirstRender = (cb: () => void) => {
  const isFirstRender = useIsFirstRender();

  useLayoutEffect(() => {
    if (!isFirstRender) {
      return;
    }
    cb();
  }, [isFirstRender]);
};

export const useCurrentScene = () => {
  return useSelector((s) => {
    const currentScene = Object.entries(s.sceneStates).find(
      ([, sceneState]) => sceneState?.stage === SceneStage.Entered,
    )?.[0];
    return currentScene;
  });
};

export const useIsCurrentScene = (scene: SceneId) => {
  const currentScene = useCurrentScene();
  return currentScene === scene;
};

export const useSceneState = (scene: SceneId) => {
  return useSelector((s) => {
    const currentScene = Object.entries(s.sceneStates).find(
      ([key]) => key === scene,
    )?.[1];
    return currentScene;
  });
};

export const useScene = (cb: EffectCallback, scene: SceneId) => {
  const currentScene = useCurrentScene();

  useLayoutEffect(() => {
    if (scene !== currentScene) return noop;

    return cb();
  }, [scene, currentScene]);
};

export const useFirstScene = (cb: EffectCallback, scene: SceneId) => {
  const currentScene = useCurrentScene();
  const isFirstTime = useRef(true);

  useLayoutEffect(() => {
    if (!isFirstTime.current) return noop;
    if (scene !== currentScene) return noop;

    isFirstTime.current = false;

    return cb();
  }, [scene, currentScene]);
};

export const useFirstSceneEntering = (cb: EffectCallback, scene: SceneId) => {
  const sceneState = useSceneState(scene);
  const isFirst = useRef(false);

  useLayoutEffect(() => {
    if (isFirst.current || sceneState?.stage !== SceneStage.Entering) {
      return noop;
    }

    isFirst.current = true;
    return cb();
  }, [sceneState]);
};

export const useSceneEntering = (cb: EffectCallback, scene: SceneId) => {
  const sceneState = useSceneState(scene);

  useLayoutEffect(() => {
    if (sceneState?.stage !== SceneStage.Entering) {
      return noop;
    }

    return cb();
  }, [sceneState?.stage]);
};

export const useSceneMounting = (cb: EffectCallback, scene: SceneId) => {
  const sceneState = useSceneState(scene);

  useLayoutEffect(() => {
    if (sceneState?.stage !== SceneStage.Mounting) {
      return noop;
    }

    return cb();
  }, [sceneState?.stage]);
};

export const useModule = <T = FunctionalComponent>(
  key: ModuleKeyDescriptor,
) => {
  const { context } = useCheckoutContext();

  const [isLoading, setIsLoading] = useState(false);
  const [Module, setModule] = useState<{ comp: T | null }>({ comp: null });

  useLayoutEffect(() => {
    (async () => {
      const module = context?.moduleFactory.getModule(key);
      if (module) {
        setIsLoading(true);
        const LoadedModule = (await module.import()) as T;
        setModule({ comp: LoadedModule });
        setIsLoading(false);
      }
    })();
  }, []);

  return {
    Module: Module.comp,
    isLoading,
  };
};

export const useDisableComponent = ({ scene }: { scene: Scene | string }) => {
  const isLoading = useSelector((s) => s.isLoading);

  const sceneState = useSceneState(scene as Scene);
  const stage = sceneState?.stage;

  const disableControls = isLoading;

  return {
    disableControls,
    disableNavigation:
      isLoading ||
      stage === SceneStage.Entering ||
      stage === SceneStage.Exiting,
  };
};
